import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import {
  identity,
  not,
  prop,
  sort,
} from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Clear } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material'

import { pluralize, singularize, titleize } from 'inflection'

import { annotationLabels, annotationTypes, defaultAllowedTypes } from '~/src/Annotations/constants'
import { useAllowedAnnotationTypes } from '~/src/Annotations/utils'
import { Allowed } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { idAndNameShape, idPropType } from '~/src/Lib/PropTypes'
import {
  debounce,
  EMPTY_OBJECT,
  memoize,
  shallowEquals,
  shallowEqualsMemoizer,
  useControlledState,
} from '~/src/Lib/Utils'
import { getTextSort } from '~/src/Lib/Utils/arrays'
import { roomOptionSort } from '~/src/Room/utils'
import ArrowDown from '~/src/UI/Icons/ArrowDown'
import DataTypeName from '~/src/UI/Shared/DataTypeName'
import SafeControl from '~/src/UI/Shared/Form/SafeControl'
import SearchField from '~/src/UI/Shared/Form/SearchField'
import ResponsiveChooser from '~/src/UI/Shared/ResponsiveChooser'
import Select from '~/src/UI/Shared/Select'
import ToggleChip from '~/src/UI/Shared/ToggleChip'
import Tooltip from '~/src/UI/Shared/Tooltip'

import {
  ALERTS_TAB,
  dateFilters,
  EVERYTHING_TAB,
  TASKS_TAB,
} from './constants'

const displayName = 'JournalFilters'
const logger = createLogger(displayName)

const ROOMS_AMOUNT = 5
const FILTER_TOGGLE_DEBOUNCE_INTERVAL = 1000
const INCLUDES_TASKS_TABS = [EVERYTHING_TAB, TASKS_TAB]

const defaultDebounceOptions = {
  leading: false,
  interval: FILTER_TOGGLE_DEBOUNCE_INTERVAL,
  minSelected: 1,
  sort: sort(getTextSort(identity))
}

const createDebouncedHandlerFactory = shallowEqualsMemoizer(({
  key,
  onParamsChange,
  setLocal,
  config = EMPTY_OBJECT,
}) => {
  const options = { ...defaultDebounceOptions, ...config }
  const debouncedOnChange = debounce(currentActive => {
    onParamsChange({
      [key]: currentActive,
    })
  }, options.interval, options.leading)
  return memoize(input => () => {
    setLocal(old => {
      const current = old.includes(input)
        ? old.filter(t => t !== input)
        : [...old, input]

      if (!options.minSelected || current.length >= options.minSelected) {
        debouncedOnChange(current)
      }

      return current.length ? options.sort(current) : old
    })
  })
}, { depth: 2 })

const FilterButton = ({
  option: { label },
  ...buttonProps
}) => <ToggleChip {...buttonProps} label={label} />

// eslint-disable-next-line react/require-default-props
const Filters = props => {
  const {
    category,
    classes,
    facilityLocationsTerm,
    types,
    searchTerms,
    me,
    relatedUsers,
    setRelatedUser,
    relatedUser,
    showComplete,
    rooms,
    dateFilter,
    tabData,
    taskCategories,
    handleFilterByDate,
    onParamsChange,
  } = props
  const allowedAnnotationTypes = useAllowedAnnotationTypes()

  const [activeTypes, setActiveTypes] = useState(types)
  const allowedCategories = Object.values(taskCategories)
    .filter(({ taskBehavior }) => taskBehavior !== 'READING_CHECKLIST') // Filters out manual reading checklist as clicking on these tasks break the app

  useEffect(() => {
    setActiveTypes(active => {
      if (shallowEquals(types, active)) return active
      return types
    })
  }, [types])

  const toggleActiveType = aType => {
    const newActiveTypes = activeTypes.includes(aType) ? activeTypes.filter(type => type !== aType) : [...activeTypes, aType]
    setActiveTypes(newActiveTypes)
  }
  const [activeCheckedRooms, setActiveCheckedRooms] = useControlledState(props, 'checkedRooms')
  const [showAllRooms, setShowAllRooms] = useState(false)

  const toggleShowAllRooms = useCallback(() => setShowAllRooms(not), [setShowAllRooms])
  const handleCategoryChange = useCallback(newValue => onParamsChange({
    category: newValue
  }), [onParamsChange])

  const handleTypeFilterReset = useCallback(() => {
    if (!tabData || !tabData.params) return
    setActiveTypes(tabData.params.types)
  }, [tabData])

  const handleEmptySearch = useCallback(() => onParamsChange({ searchTerms: '' }), [onParamsChange])
  const toggleShowComplete = useCallback(() => onParamsChange(
    ({ showComplete: old }) => ({ showComplete: !old })
  ), [onParamsChange])

  const roomsToShow = useMemo(() => {
    const showable = showAllRooms ? Object.values(rooms) : Object.values(rooms).slice(0, ROOMS_AMOUNT)
    return roomOptionSort(showable)
  }, [rooms, showAllRooms])

  const getRoomClickCallback = createDebouncedHandlerFactory({
    key: 'checkedRooms',
    onParamsChange,
    setLocal: setActiveCheckedRooms,
    config: { minSelected: 0, sort: sort((a, b) => a - b) },
  })

  const toggleShowUser = useCallback(() => onParamsChange(
    ({ showUser: old }) => ({ showUser: !old })
  ), [onParamsChange])

  const handleSelectedUser = useCallback(selectedUser => onParamsChange({
    relatedUser: selectedUser
  }), [onParamsChange])

  const handleRelatedUser = useCallback(selectedUser => {
    if (!relatedUser) {
      toggleShowUser()
    }
    setRelatedUser(selectedUser)
    handleSelectedUser(selectedUser)
  }, [handleSelectedUser, relatedUser, setRelatedUser, toggleShowUser])

  const clearRelatedUser = useCallback(() => {
    toggleShowUser()
    setRelatedUser()
    handleSelectedUser()
  }, [handleSelectedUser, setRelatedUser, toggleShowUser])

  // simple debounce implementation when picking type-related filters
  useEffect(() => {
    if (shallowEquals(types, activeTypes)) return () => {}
    const handler = setTimeout(() => {
      onParamsChange({ types: activeTypes })
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [activeTypes, onParamsChange, types])

  const locationsTerm = facilityLocationsTerm(rooms)

  return (
    <div className={classes.filters}>
      <div className={classNames(classes.filtersScrollableArea, 'scroller')}>
        <SearchField
          className={classes.searchField}
          size="small"
          fullWidth
          placeholder={i18n`Search journal`}
          onChange={onParamsChange}
          onEmpty={handleEmptySearch}
          value={searchTerms}
          variant="outlined"
        />
        {tabData.value === EVERYTHING_TAB ? (
          <div className={classes.annotationTypesFilters}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography gutterBottom>{i18n`Filter by type`}</Typography>
              <Button
                color="primary"
                onClick={handleTypeFilterReset}
                classes={{
                  root: classNames({
                    [classes.resetButtonHidden]: shallowEquals(activeTypes, tabData.params.types)
                  })
                }}
              >
                {i18n`reset`}
              </Button>
            </Box>
            {defaultAllowedTypes.map(aType => {
              if (!allowedAnnotationTypes.has(aType)) return null
              const isSelected = activeTypes.find(type => type === aType)
              const Icon = annotationTypes.filter(obj => obj.type === aType)[0].Icon
              return (
                <Chip
                  key={aType}
                  style={{ borderWidth: isSelected ? '1px' : '0px' }}
                  className={classes.annotationTypeFilter}
                  color="primary"
                  variant="outlined"
                  label={(
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {!!Icon && <Icon />}
                      <DataTypeName style={{ marginLeft: 4 }} name={annotationLabels[aType]} />
                    </div>
                  )}
                  onClick={() => toggleActiveType(aType)}
                />
              )
            })}
          </div>
        ) : null}
        {tabData.value === TASKS_TAB ? (
          <>
            <Allowed negate="tiers" tiers="GO">
              <div className={classes.annotationTypesFilters}>
                <Typography gutterBottom color="textSecondary">{i18n`Task type`}</Typography>
                <ResponsiveChooser
                  vertical
                  maxButtons={5}
                  multiple={false}
                  label={i18n`filter by type`}
                  Button={FilterButton}
                  options={[
                    { label: i18n`All`, value: null },
                    ...allowedCategories.map(({ name: label, id: value }) => ({ label, value }))
                  ]}
                  value={category}
                  onChange={handleCategoryChange}
                />
              </div>
            </Allowed>
            <div className={classes.annotationTypesFilters}>
              <Typography gutterBottom color="textSecondary">{i18n`Filter by date`}</Typography>
              <ResponsiveChooser
                vertical
                maxButtons={5}
                multiple={false}
                label={i18n`filter by date`}
                Button={FilterButton}
                options={dateFilters}
                value={dateFilter}
                onChange={handleFilterByDate}
              />
            </div>
          </>
        ) : null}
        {tabData.value !== ALERTS_TAB && (
          <div className={classes.relatedUser}>
            <Select
              fullWidth
              key="relatedUser"
              label={i18n`Related user`}
              value={relatedUser}
              options={[
                { label: me?.fullName, value: me?.id },
                ...relatedUsers.filter(ru => ru?.fullName !== me?.fullName).map(({ id: value, fullName: label }) => ({ label, value })),
              ]}
              onChange={selectedUser => { handleRelatedUser(selectedUser) }}
              buttonProps={{
                endIcon: (
                  <Box display="flex" gap="1ch">
                    {relatedUser ? (
                      <SafeControl>
                        <IconButton
                          className={classes.clearBtn}
                          color="primary"
                          onClick={clearRelatedUser}
                          size="large"
                        >
                          <Clear />
                        </IconButton>
                      </SafeControl>
                    ) : null}
                    <ArrowDown />
                  </Box>
                )
              }}
            />
          </div>
        )}
        <Allowed negate="tiers" tiers="GO">
          {INCLUDES_TASKS_TABS.includes(tabData.value) ? (
            <FormControlLabel
              key="complete"
              value="showComplete"
              control={<Switch checked={showComplete} onChange={toggleShowComplete} />}
              label={i18n`Show completed tasks`}
              labelPlacement="start"
              classes={{ root: classes.switchBtn }}
            />
          ) : null}
        </Allowed>
        <div className={classes.location}>
          <Typography
            display="inline"
          >
            {titleize(singularize(locationsTerm))}
          </Typography>
          {roomsToShow.map(room => {
            const option = (
              <FormControlLabel
                key={room.id}
                classes={{ root: classes.checkBtn }}
                control={(
                  <Checkbox
                    checked={activeCheckedRooms.includes(room.id)}
                    name={room.name}
                    onChange={getRoomClickCallback(room.id)}
                  />
                )}
                label={room.name}
                labelPlacement="start"
                slotProps={{ typography: { color: room.archived ? 'textSecondary' : 'textPrimary' } }}
              />
            )
            return room.archived ? (
              <Tooltip
                key={room.id}
                title={i18n`This ${facilityLocationsTerm('room')} is archived`}
                variant="light"
              >
                {option}
              </Tooltip>
            ) : option
          })}
          {Object.values(rooms).length > ROOMS_AMOUNT && (
            <Button variant="text" color="primary" onClick={toggleShowAllRooms}>
              {`Show ${showAllRooms ? i18n`fewer` : i18n`more`} ${pluralize(locationsTerm)}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

Filters.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  facilityLocationsTerm: PropTypes.func.isRequired,
  rooms: PropTypes.objectOf(idAndNameShape).isRequired,
  tabData: PropTypes.shape({
    value: PropTypes.string,
    params: PropTypes.shape({
      types: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  taskCategories: PropTypes.objectOf(idAndNameShape).isRequired,
  category: idPropType.nullable,
  dateFilter: PropTypes.oneOf(dateFilters.map(prop('value'))).isRequired,
  searchTerms: PropTypes.string.isRequired,
  showComplete: PropTypes.bool.isRequired,
  me: PropTypes.shape({
    id: idPropType,
    fullName: PropTypes.string
  }).isRequired,
  relatedUsers: PropTypes.arrayOf(idAndNameShape).isRequired,
  relatedUser: idPropType.nullable,
  setRelatedUser: PropTypes.func.isRequired,
  // Used in useControlledState
  /* eslint-disable react/no-unused-prop-types */
  checkedRooms: PropTypes.arrayOf(PropTypes.number).isRequired,
  types: PropTypes.arrayOf(PropTypes.string).isRequired,
  /* eslint-enable react/no-unused-prop-types */
  handleFilterByDate: PropTypes.func.isRequired,
  onParamsChange: PropTypes.func.isRequired,
}
Filters.defaultProps = {
  category: null,
  relatedUser: null
}

Filters.displayName = displayName

const Wrapped = React.memo(props => {
  const { taskCategories } = useConnect('selectTaskCategories')

  return <Filters {...props} taskCategories={taskCategories} />
})

export default Wrapped
